import { cltw } from 'ui';
import FeatureList from '../FeatureList/FeatureList';
import {
  getFeatureListsToShow,
  getNumberOfPlaceholdersToShow,
} from '../membershipComparisonUtils';
import type {
  CompareTableSubcopy,
  MembershipTier,
  MembershipTierComparisonFeatureList,
  MembershipTierWithTrial,
} from '@/lib/constants/maverick';

interface TierFeatureListProps {
  tiers: MembershipTierWithTrial[];
  selectedTiers: MembershipTierWithTrial[];
  softSelectedTier: MembershipTier;
  membershipTierFeatureLists: MembershipTierComparisonFeatureList[];
  onSelect: (tierName: MembershipTierWithTrial) => void;
  isAddingToCart: boolean;
  formattedTierPrices?: Record<string, string>;
  compareTableSubcopyData?: CompareTableSubcopy[];
  isUpgrade?: boolean;
}

export default function TierFeatureLists({
  tiers,
  softSelectedTier,
  selectedTiers,
  membershipTierFeatureLists,
  onSelect,
  isAddingToCart,
  formattedTierPrices = {},
  compareTableSubcopyData = [],
  isUpgrade = false,
}: TierFeatureListProps): React.JSX.Element {
  const featureListsToShowDesktop = getFeatureListsToShow(
    tiers,
    membershipTierFeatureLists,
  );
  const featureListsToShowMobile = getFeatureListsToShow(
    selectedTiers,
    membershipTierFeatureLists,
  );

  return (
    <div
      className='flex h-full w-full flex-row justify-center'
      data-testid='tier-feature-lists'
    >
      {/* Mobile feature lists -- only selected tiers*/}
      <div className='desktop:hidden relative mt-4 flex w-full flex-row items-start justify-center gap-6 text-center'>
        {featureListsToShowMobile.map((featureList) => {
          return (
            <FeatureList
              compareTableSubcopyData={compareTableSubcopyData}
              featureList={featureList}
              formattedTierPrice={formattedTierPrices[featureList.tier]}
              isAddingToCart={isAddingToCart}
              isUpgrade={isUpgrade}
              key={`feature-list-tier-${featureList.tier}-mobile`}
              numPlaceholdersToShow={getNumberOfPlaceholdersToShow(
                featureListsToShowMobile,
                featureList.featuresCollection.items.length,
              )}
              onSelect={onSelect}
            />
          );
        })}
      </div>
      {/* Desktop feature lists -- all tiers*/}
      <div
        className={cltw(
          'desktop:flex relative hidden w-full flex-row items-start justify-center gap-6 text-center',
          tiers.length === 2 ? 'w-[720px]' : 'w-full',
        )}
      >
        {featureListsToShowDesktop.map((featureList) => {
          return (
            <div
              className='relative w-full'
              key={`feature-list-tier-${featureList.tier}-desktop`}
            >
              {/* Render shadow behind soft selected tier */}
              {featureList.tier === softSelectedTier && (
                <div className='absolute -bottom-2 -top-14 z-0 w-full rounded-xl bg-gray-50' />
              )}
              <FeatureList
                compareTableSubcopyData={compareTableSubcopyData}
                featureList={featureList}
                formattedTierPrice={formattedTierPrices[featureList.tier]}
                isAddingToCart={isAddingToCart}
                isUpgrade={isUpgrade}
                numPlaceholdersToShow={getNumberOfPlaceholdersToShow(
                  featureListsToShowDesktop,
                  featureList.featuresCollection.items.length,
                )}
                onSelect={onSelect}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
